import { PropertyData } from '@/services/scraper.service';
import {
  AREA_TOLERANCE_M2,
  PRICE_TOLERANCE_PERCENT,
  MIN_UNIQUE_PROPERTIES_FOR_CONDO,
  MIN_CONDO_NAME_LENGTH,
  MAX_CONDO_NAME_LENGTH,
  MIN_WORD_LENGTH,
  CONDOMINIUM_PATTERNS,
  IGNORE_WORDS,
  GENERIC_NAMES,
} from './propertyGrouping.constants';
import {
  mergeCondominiumsByProximity,
  associateLoosePropertiesToCondos,
} from './propertyGrouping.merge';

export interface PropertyGroup {
  id: string;
  condominiumName: string | null;
  address: string;
  latitude: number | null;
  longitude: number | null;
  properties: PropertyData[];
  totalCount: number;
  selectedCount: number;
}

export interface StreetGroup {
  id: string;
  streetName: string;
  displayName: string; // Nome formatado para exibicao
  latitude: number | null;
  longitude: number | null;
  condominiums: PropertyGroup[]; // Condominios nesta rua
  looseProperties: PropertyData[]; // Imoveis avulsos (sem condominio)
  totalCount: number;
  selectedCount: number;
}

export function extractCondominiumName(title: string): string | null {
  if (!title) return null;

  const normalizedTitle = title.trim();

  for (let i = 0; i < CONDOMINIUM_PATTERNS.length; i++) {
    const pattern = CONDOMINIUM_PATTERNS[i];
    if (!pattern) continue;
    const match = normalizedTitle.match(pattern);

    if (match && match[1]) {
      let name = match[1].trim();

      // Remover palavras ignoradas do final do nome
      const words = name.split(/\s+/);
      const filteredWords: string[] = [];

      for (const word of words) {
        const wordLower = word.toLowerCase();
        // Parar se encontrar palavra ignorada
        if (IGNORE_WORDS.includes(wordLower)) {
          break;
        }
        // Parar se encontrar numero (ex: "3 dorms")
        if (/^\d+$/.test(word)) {
          break;
        }
        filteredWords.push(word);
      }

      if (filteredWords.length === 0) {
        continue;
      }

      name = filteredWords.join(' ');

      // Verificar se tem pelo menos uma palavra valida (nao ignorada, > MIN_WORD_LENGTH chars)
      const hasValidWord = filteredWords.some(word =>
        word.length > MIN_WORD_LENGTH && !IGNORE_WORDS.includes(word.toLowerCase())
      );

      // Verificar se o nome é muito generico
      const isGenericName = filteredWords.length === 1 &&
        GENERIC_NAMES.includes(filteredWords[0]?.toLowerCase() || '');

      if (isGenericName) {
        continue;
      }

      if (hasValidWord && name.length >= MIN_CONDO_NAME_LENGTH && name.length <= MAX_CONDO_NAME_LENGTH) {
        return normalizeCondominiumName(name);
      }
    }
  }

  return null;
}

function normalizeCondominiumName(name: string): string {
  return name
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function normalizeAddress(address: string): string {
  return address
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/,\s*/g, ',')
    .replace(/\s*-\s*/g, '-')
    .replace(/\s+n[º°]\s*/g, ' ')  // Remove "nº" ou "n°" mas não a letra "n" sozinha
    .replace(/apartamento|apto|apt|ap\./gi, '')
    .replace(/bloco|bl\./gi, '')
    .replace(/torre|tr\./gi, '')
    .trim();
}

function extractStreetName(address: string): string {
  const normalized = normalizeAddress(address);
  const parts = normalized.split(/[,\-]/);
  return parts[0]?.trim() || normalized;
}

function extractStreetNumber(address: string): string | null {
  const normalized = normalizeAddress(address);
  const parts = normalized.split(/[,\-]/);
  if (parts.length >= 2) {
    const secondPart = parts[1]?.trim() || '';
    const numMatch = secondPart.match(/^(\d+)/);
    if (numMatch) {
      return numMatch[1] || null;
    }
  }
  return null;
}

function extractFullAddress(address: string): string {
  const street = extractStreetName(address);
  const number = extractStreetNumber(address);
  return number ? `${street} ${number}` : street;
}

function capitalize(str: string): string {
  return str.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function formatStreetDisplayName(street: string): string {
  let formattedStreet = capitalize(street);
  formattedStreet = formattedStreet
    .replace(/^Avenida\b/i, 'Av.')
    .replace(/^Alameda\b/i, 'Al.')
    .replace(/^Travessa\b/i, 'Tv.')
    .replace(/^Praca\b/i, 'Pç.')
    .replace(/^Estrada\b/i, 'Estr.')
    .replace(/^Rua\b/i, 'R.');

  return formattedStreet;
}

function formatAddressAsCondoName(street: string, number: string): string {
  const formattedStreet = formatStreetDisplayName(street);
  return `Edifício ${formattedStreet}, ${number}`;
}

export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000; // Raio da Terra em metros
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

function areAddressesEquivalent(addr1: string, addr2: string): boolean {
  const normalize = (addr: string) => addr
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[áàâãä]/g, 'a')
    .replace(/[éêëè]/g, 'e')
    .replace(/[íîïì]/g, 'i')
    .replace(/[óôõöò]/g, 'o')
    .replace(/[úûüù]/g, 'u')
    .replace(/[ç]/g, 'c')
    .replace(/[ñ]/g, 'n')
    .trim();

  const n1 = normalize(addr1);
  const n2 = normalize(addr2);

  if (n1 === n2) {
    return true;
  }

  const removePrefix = (addr: string) => addr
    .replace(/^(rua|r\.?|avenida|av\.?|alameda|al\.?|travessa|tv\.?|praca|pc\.?|estrada|estr\.?)\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();

  const clean1 = removePrefix(n1);
  const clean2 = removePrefix(n2);

  if (clean1 === clean2) {
    return true;
  }

  if (clean1.length > 3 && clean2.length > 3) {
    if (clean1.includes(clean2) || clean2.includes(clean1)) {
      return true;
    }
  }

  if (clean1.length >= 5 && clean2.length >= 5) {
    const words1 = clean1.split(' ').filter(w => w.length > 2);
    const words2 = clean2.split(' ').filter(w => w.length > 2);
    const commonWords = words1.filter(w => words2.includes(w));
    if (commonWords.length >= 2 || (commonWords.length === 1 && words1.length <= 2 && words2.length <= 2)) {
      return true;
    }
  }

  return false;
}

function arePropertiesDuplicates(prop1: PropertyData, prop2: PropertyData): boolean {
  const area1 = prop1.metragem_num || 0;
  const area2 = prop2.metragem_num || 0;
  const price1 = prop1.valor_venda_num || 0;
  const price2 = prop2.valor_venda_num || 0;

  const hasArea = area1 > 0 && area2 > 0;
  const hasPrice = price1 > 0 && price2 > 0;

  if (!hasArea && !hasPrice) {
    return false;
  }

  let areaMatch = true;
  if (hasArea) {
    const areaDiff = Math.abs(area1 - area2);
    areaMatch = areaDiff <= AREA_TOLERANCE_M2;
  }

  let priceMatch = true;
  if (hasPrice) {
    const priceDiff = Math.abs(price1 - price2);
    const priceAvg = (price1 + price2) / 2;
    priceMatch = priceDiff / priceAvg <= PRICE_TOLERANCE_PERCENT;
  }

  if (hasArea && hasPrice) {
    return areaMatch && priceMatch;
  }
  return hasArea ? areaMatch : priceMatch;
}

function countUniqueProperties(propertiesAtAddress: PropertyData[]): number {
  if (propertiesAtAddress.length <= 1) return propertiesAtAddress.length;

  const groups: PropertyData[][] = [];

  for (const prop of propertiesAtAddress) {
    let foundGroup = false;

    for (const group of groups) {
      if (group[0] && arePropertiesDuplicates(prop, group[0])) {
        group.push(prop);
        foundGroup = true;
        break;
      }
    }

    if (!foundGroup) {
      groups.push([prop]);
    }
  }

  return groups.length;
}

export function groupPropertiesByCondominium(
  properties: PropertyData[],
  selectedProperties: Set<string>
): PropertyGroup[] {
  const propertiesByAddress = new Map<string, PropertyData[]>();
  for (const property of properties) {
    const fullAddress = extractFullAddress(property.endereco_completo);
    if (!propertiesByAddress.has(fullAddress)) {
      propertiesByAddress.set(fullAddress, []);
    }
    propertiesByAddress.get(fullAddress)!.push(property);
  }

  const uniqueCountByAddress = new Map<string, number>();
  for (const [address, props] of propertiesByAddress) {
    const uniqueCount = countUniqueProperties(props);
    uniqueCountByAddress.set(address, uniqueCount);
  }

  const groups = new Map<string, PropertyGroup>();

  for (const property of properties) {
    const condoName = extractCondominiumName(property.titulo);
    const streetName = extractStreetName(property.endereco_completo);
    const streetNumber = extractStreetNumber(property.endereco_completo);
    const fullAddress = extractFullAddress(property.endereco_completo);
    const uniqueCount = uniqueCountByAddress.get(fullAddress) || 1;

    let groupKey: string;
    let groupCondoName: string | null = condoName;
    let groupAddress: string;

    if (condoName) {
      groupKey = `condo_${condoName}`;
      groupAddress = streetName;
    } else if (streetNumber && uniqueCount >= MIN_UNIQUE_PROPERTIES_FOR_CONDO) {
      const autoCondoName = formatAddressAsCondoName(streetName, streetNumber);
      groupKey = `condo_auto_${fullAddress}`;
      groupCondoName = autoCondoName;
      groupAddress = streetName;
    } else {
      groupKey = `street_${streetName}`;
      groupCondoName = null;
      groupAddress = streetName;
    }

    if (groupCondoName) {
      for (const [existingKey, existingGroup] of groups) {
        if (existingGroup.condominiumName === groupCondoName) {
          groupKey = existingKey;
          break;
        }
      }
    }

    if (!groupCondoName) {
      for (const [existingKey, existingGroup] of groups) {
        if (!existingGroup.condominiumName && areAddressesEquivalent(existingGroup.address, groupAddress)) {
          groupKey = existingKey;
          break;
        }
      }
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        id: groupKey,
        condominiumName: groupCondoName,
        address: groupAddress,
        latitude: property.latitude || null,
        longitude: property.longitude || null,
        properties: [],
        totalCount: 0,
        selectedCount: 0,
      });
    }

    const group = groups.get(groupKey)!;
    group.properties.push(property);
    group.totalCount++;

    if (selectedProperties.has(property.url)) {
      group.selectedCount++;
    }

    if (!group.condominiumName && condoName) {
      group.condominiumName = condoName;
    }

    if (!group.latitude && property.latitude) {
      group.latitude = property.latitude;
    }
    if (!group.longitude && property.longitude) {
      group.longitude = property.longitude;
    }
  }

  const groupArray = Array.from(groups.values());

  groupArray.sort((a, b) => {
    if (a.condominiumName && !b.condominiumName) return -1;
    if (!a.condominiumName && b.condominiumName) return 1;

    if (b.totalCount !== a.totalCount) {
      return b.totalCount - a.totalCount;
    }

    const avgScoreA = a.properties.reduce((sum, p) => sum + (p.score_similaridade || 0), 0) / a.properties.length;
    const avgScoreB = b.properties.reduce((sum, p) => sum + (p.score_similaridade || 0), 0) / b.properties.length;
    return avgScoreB - avgScoreA;
  });

  for (const group of groupArray) {
    group.properties.sort((a, b) => (b.score_similaridade || 0) - (a.score_similaridade || 0));
  }

  return groupArray;
}

export function prioritizeSimilarProperties(
  properties: PropertyData[],
  referenceArea?: number | null,
  referencePrice?: number | null,
  referenceBedrooms?: number | null
): PropertyData[] {
  if (!referenceArea && !referencePrice && !referenceBedrooms) {
    return properties;
  }

  return [...properties].sort((a, b) => {
    let scoreA = 0;
    let scoreB = 0;

    if (referenceArea && referenceArea > 0) {
      const areaDiffA = Math.abs((a.metragem_num || 0) - referenceArea) / referenceArea;
      const areaDiffB = Math.abs((b.metragem_num || 0) - referenceArea) / referenceArea;
      scoreA += (1 - Math.min(areaDiffA, 1)) * 40;
      scoreB += (1 - Math.min(areaDiffB, 1)) * 40;
    }

    if (referencePrice && referencePrice > 0) {
      const priceDiffA = Math.abs((a.valor_venda_num || 0) - referencePrice) / referencePrice;
      const priceDiffB = Math.abs((b.valor_venda_num || 0) - referencePrice) / referencePrice;
      scoreA += (1 - Math.min(priceDiffA, 1)) * 30;
      scoreB += (1 - Math.min(priceDiffB, 1)) * 30;
    }

    if (referenceBedrooms && referenceBedrooms > 0) {
      const bedroomDiffA = Math.abs((a.quartos_num || 0) - referenceBedrooms);
      const bedroomDiffB = Math.abs((b.quartos_num || 0) - referenceBedrooms);
      scoreA += Math.max(0, 20 - bedroomDiffA * 5);
      scoreB += Math.max(0, 20 - bedroomDiffB * 5);
    }

    scoreA += (a.score_similaridade || 0) * 0.1;
    scoreB += (b.score_similaridade || 0) * 0.1;

    return scoreB - scoreA;
  });
}

export function calculateGroupStats(properties: PropertyData[]) {
  const validPrices = properties
    .map(p => p.valor_venda_num)
    .filter((p): p is number => p !== null && p !== undefined && p > 0);

  const validAreas = properties
    .map(p => p.metragem_num)
    .filter((a): a is number => a !== null && a !== undefined && a > 0);

  const avgPrice = validPrices.length > 0
    ? validPrices.reduce((a, b) => a + b, 0) / validPrices.length
    : null;

  const avgArea = validAreas.length > 0
    ? validAreas.reduce((a, b) => a + b, 0) / validAreas.length
    : null;

  const avgPricePerM2 = avgPrice && avgArea ? avgPrice / avgArea : null;

  return {
    avgPrice,
    avgArea,
    avgPricePerM2,
    minPrice: validPrices.length > 0 ? Math.min(...validPrices) : null,
    maxPrice: validPrices.length > 0 ? Math.max(...validPrices) : null,
    minArea: validAreas.length > 0 ? Math.min(...validAreas) : null,
    maxArea: validAreas.length > 0 ? Math.max(...validAreas) : null,
  };
}

export function groupPropertiesByStreet(
  properties: PropertyData[],
  selectedProperties: Set<string>
): StreetGroup[] {
  const propertiesByStreet = new Map<string, PropertyData[]>();
  for (const property of properties) {
    const streetName = extractStreetName(property.endereco_completo);
    if (!propertiesByStreet.has(streetName)) {
      propertiesByStreet.set(streetName, []);
    }
    propertiesByStreet.get(streetName)!.push(property);
  }

  const streetGroups = new Map<string, { streetName: string; properties: PropertyData[] }>();
  for (const [streetName, props] of propertiesByStreet) {
    let foundEquivalent = false;

    for (const [, existingGroup] of streetGroups) {
      if (areAddressesEquivalent(existingGroup.streetName, streetName)) {
        if (streetName.length > existingGroup.streetName.length) {
          existingGroup.streetName = streetName;
        }
        existingGroup.properties.push(...props);
        foundEquivalent = true;
        break;
      }
    }

    if (!foundEquivalent) {
      streetGroups.set(streetName, { streetName, properties: props });
    }
  }

  const result: StreetGroup[] = [];

  for (const [_, streetData] of streetGroups) {
    const { streetName, properties: streetProperties } = streetData;

    const propertiesByFullAddress = new Map<string, PropertyData[]>();
    for (const property of streetProperties) {
      const fullAddress = extractFullAddress(property.endereco_completo);
      if (!propertiesByFullAddress.has(fullAddress)) {
        propertiesByFullAddress.set(fullAddress, []);
      }
      propertiesByFullAddress.get(fullAddress)!.push(property);
    }

    const condominiums: PropertyGroup[] = [];
    const looseProperties: PropertyData[] = [];
    const processedUrls = new Set<string>();

    const condoNameGroups = new Map<string, PropertyData[]>();
    for (const property of streetProperties) {
      const condoName = extractCondominiumName(property.titulo);
      if (condoName) {
        if (!condoNameGroups.has(condoName)) {
          condoNameGroups.set(condoName, []);
        }
        condoNameGroups.get(condoName)!.push(property);
        processedUrls.add(property.url);
      }
    }

    for (const [condoName, props] of condoNameGroups) {
      const firstProp = props[0];
      condominiums.push({
        id: `condo_${condoName}_${streetName}`,
        condominiumName: condoName,
        address: streetName,
        latitude: firstProp?.latitude || null,
        longitude: firstProp?.longitude || null,
        properties: props,
        totalCount: props.length,
        selectedCount: props.filter(p => selectedProperties.has(p.url)).length,
      });
    }

    for (const [fullAddress, props] of propertiesByFullAddress) {
      const unprocessedProps = props.filter(p => !processedUrls.has(p.url));
      if (unprocessedProps.length === 0) continue;

      const firstPropAddress = unprocessedProps[0]?.endereco_completo || '';
      const streetNumber = extractStreetNumber(firstPropAddress);
      const uniqueCount = countUniqueProperties(unprocessedProps);

      if (streetNumber && uniqueCount >= MIN_UNIQUE_PROPERTIES_FOR_CONDO) {
        const autoCondoName = formatAddressAsCondoName(streetName, streetNumber);
        const firstProp = unprocessedProps[0];

        condominiums.push({
          id: `condo_auto_${fullAddress}`,
          condominiumName: autoCondoName,
          address: streetName,
          latitude: firstProp?.latitude || null,
          longitude: firstProp?.longitude || null,
          properties: unprocessedProps,
          totalCount: unprocessedProps.length,
          selectedCount: unprocessedProps.filter(p => selectedProperties.has(p.url)).length,
        });

        unprocessedProps.forEach(p => processedUrls.add(p.url));
      } else {
        looseProperties.push(...unprocessedProps);
        unprocessedProps.forEach(p => processedUrls.add(p.url));
      }
    }

    const mergedCondominiums = mergeCondominiumsByProximity(condominiums, selectedProperties);

    const { finalCondominiums, finalLooseProperties } = associateLoosePropertiesToCondos(
      mergedCondominiums,
      looseProperties,
      selectedProperties
    );

    finalCondominiums.sort((a, b) => b.totalCount - a.totalCount);

    for (const condo of finalCondominiums) {
      condo.properties.sort((a, b) => (b.score_similaridade || 0) - (a.score_similaridade || 0));
    }

    finalLooseProperties.sort((a, b) => (b.score_similaridade || 0) - (a.score_similaridade || 0));

    const firstProperty = streetProperties[0];

    const totalCount = streetProperties.length;
    const selectedCount = streetProperties.filter(p => selectedProperties.has(p.url)).length;

    result.push({
      id: `street_${streetName}`,
      streetName,
      displayName: formatStreetDisplayName(streetName),
      latitude: firstProperty?.latitude || null,
      longitude: firstProperty?.longitude || null,
      condominiums: finalCondominiums,
      looseProperties: finalLooseProperties,
      totalCount,
      selectedCount,
    });
  }

  result.sort((a, b) => {
    if (a.condominiums.length !== b.condominiums.length) {
      return b.condominiums.length - a.condominiums.length;
    }
    return b.totalCount - a.totalCount;
  });

  return result;
}
